/**
 * <Glitch> — CSS class-toggled glitch wrapper.
 *
 * Replaces the previous Framer Motion variant approach with a simple
 * state-driven `.glitch-active` class toggle. The actual animation lives
 * entirely in CSS (see index.css — `.glitch-text` / `.glitch-text.glitch-active`).
 *
 * Usage:
 *   <Glitch text="projects" trigger="hover">projects</Glitch>
 *   <Glitch text="Building." trigger="ambient">Building.</Glitch>
 *
 * Props:
 *   text      – must match the visible text (used for data-text on the pseudo-elements)
 *   trigger   – "hover" | "ambient"
 *   interval  – [minMs, maxMs] for ambient mode (default 8000–15000)
 *   children  – rendered content
 *   className – forwarded to the outer span
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { glitchManager } from '@/lib/glitchManager'

// Detect prefers-reduced-motion once at module load so we avoid a hook call per-instance
function prefersReducedMotion() {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

interface GlitchProps {
    children: ReactNode
    text?: string
    trigger?: 'hover' | 'ambient'
    /** [minMs, maxMs] — used only in ambient mode */
    interval?: [number, number]
    className?: string
}

export default function Glitch({
    children,
    text,
    trigger = 'hover',
    interval = [8000, 15000],
    className = '',
}: GlitchProps) {
    const [glitching, setGlitching] = useState(false)
    const hoverCooldownRef = useRef(false)
    const ambientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Derive data-text from prop or from the plain string child
    const dataText = text ?? (typeof children === 'string' ? children : '')

    /* ── hover trigger ─────────────────────────────────────── */
    const handleHover = () => {
        if (trigger !== 'hover') return
        if (prefersReducedMotion()) return
        if (hoverCooldownRef.current || glitching) return

        hoverCooldownRef.current = true
        setGlitching(true)

        // Glitch for 120 ms then clear
        setTimeout(() => setGlitching(false), 120)

        // Cooldown 800 ms so rapid re-hovers don't spam
        setTimeout(() => { hoverCooldownRef.current = false }, 800)
    }

    /* ── ambient trigger ───────────────────────────────────── */
    useEffect(() => {
        if (trigger !== 'ambient') return
        if (prefersReducedMotion()) return

        const schedule = () => {
            const delay = interval[0] + Math.random() * (interval[1] - interval[0])
            ambientTimerRef.current = setTimeout(() => {
                if (glitchManager.acquire(1200)) {
                    setGlitching(true)
                    // Glitch for 200 ms, then release lock
                    setTimeout(() => {
                        setGlitching(false)
                        glitchManager.release()
                    }, 200)
                }
                schedule()
            }, delay)
        }

        schedule()
        return () => {
            if (ambientTimerRef.current) clearTimeout(ambientTimerRef.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trigger])

    return (
        <span
            data-text={dataText}
            className={[
                'glitch-text',
                glitching ? 'glitch-active' : '',
                className,
            ].filter(Boolean).join(' ')}
            onMouseEnter={handleHover}
            onFocus={handleHover}
        >
            {children}
        </span>
    )
}
